import { useNavigate, useParams} from "react-router-dom";
import { useUserContext } from "../providers/UserContext";
import { getButtonClassName } from "../components/common/Button";
import {useCallback, useEffect, useState} from "react";
import { viewSidebarItem } from "../lib/database";
import {Activity, Chat, UnreadChat} from "../lib/types";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { useChatListContext } from "../providers/ChatListProvider";
import { getItem, setItem, mergeChats } from "../lib/localStorage";
import {useInboxContext} from "../providers/InboxProvider";
import {ChatPage} from "./Chat";

export const Chats = ({}: {}) => {
  const { id } = useParams();
  const { user, sidebar, activeWorkspace, setActiveChatId, unreadChats, activity, getSidebarItemName, activeChatId } = useUserContext();
  const { inbox } = useInboxContext();
  const { setChatListSettings, setChatListVisible, setLoadChats, setCurrentFilter, chatList, updateChatOnList } = useChatListContext();
  const [ sidebarItem, setSidebarItem ] = useState(sidebar.find((item) => item.id === id));
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastLoadedChatTimestamp, setLastLoadedChatTimestamp] = useState<number>(
    getItem(`lastLoadedChatTimestamp_${id}`) || 0
  );
  const { chatId } = useParams();

  const navigate = useNavigate();
  useEffect(() => {
    setCurrentFilter("sidebar_"+id || "");
    setChatListVisible(true);
    const newItem = sidebar.find((item) => item.id === id);
    if (sidebar.length > 0 && !newItem) {
      navigate("/inbox");
    } else if (newItem) {
      setSidebarItem(newItem);
    }
  }, [setChatListVisible, setActiveChatId, id, sidebar]);

  useEffect(() => {
    setActiveChatId(chatId || "");
  }, [chatId]);

  const mergeGroupChats = async (newChats: Chat[]) => {
    const localChats = getItem(`sidebarItemChats_${id}`) || [];
    return mergeChats(localChats, newChats, (mergedChats) => {
      setItem(`sidebarItemChats_${id}`, mergedChats);
      setChats(mergedChats);
      if (newChats.length > 0) {
        setItem(`lastLoadedChatTimestamp_${id}`, Date.now());
        if (user && sidebarItem) {
          viewSidebarItem(user, sidebarItem);
        }
      }
      setLastLoadedChatTimestamp(mergedChats[0]?.timestamp || 0);
      if (sidebarItem) {
        setChatListSettings(getSidebarItemName(sidebarItem, true), []);
      }
      return mergedChats;
    });
  };

  const hasNewMessagesForItem = useCallback((sidebarItemId: string, unreadChats: UnreadChat[], activity: Activity) => {
    // const lastView = getItem(`lastLoadedChatTimestamp_${sidebarItemId}`) || 0;
    // return !lastView || unreadChats.filter(chat => chat.sidebarItemId === sidebarItemId).length > 0 || (sidebarItem?.lastMessageTimestamp || 0) > lastView || (activity.chats?.[activeChatId]?.lastMessage || 0) > lastView;
    // @todo update this later to properly work and not cause a lot of redundant loads
    return true;
  }, [sidebarItem, activity]);

  const getChats = async (id:string, unreadChats: UnreadChat[], activity: Activity) => {
    const functions = getFunctions();

    const lastLoadedChatTimestamp = getItem(`lastLoadedChatTimestamp_${id}`) || 0
    const getUserChats = httpsCallable<
      { groupId?: string[]; userId: string[], timestamp?: number },
      { chats: { [key: string]: Chat } }
    >(functions, "getUserChats");
    try {
      if (!hasNewMessagesForItem(id || "", unreadChats, activity)) {
        return mergeGroupChats([]);
      }
      const result = await getUserChats({
        groupId: sidebarItem?.meaningfulGroups || [],
        userId: sidebarItem?.meaningfulUsers || [],
        timestamp: lastLoadedChatTimestamp- + 1,
      });
      const fetchedChats = Object.values(result.data.chats);
      const resultChats = mergeGroupChats(fetchedChats);
      return resultChats;
    } catch (error) {
      console.error("Error fetching group chats:", error);
      return [];
    }
  };

  const loadChats = async (attempt = 0) => {
    if (id && user && sidebarItem) {
      try {
        const chats = await getChats(id, unreadChats, activity);
        if (sidebarItem) {
          setChatListSettings(getSidebarItemName(sidebarItem, true), chats);
        }
        setLoading(false);
        return chats;
      } catch (error) {
        console.error("Error fetching group chats:", error);
      }
    }
  };

  useEffect(() => {
    if (sidebarItem) {
      loadChats();
      setLoadChats(() => (unreadChats: UnreadChat[], activity: Activity) => {
        return getChats(id || "", unreadChats, activity); // Ensure the function matches the expected signature
      });
    }
  }, [sidebarItem]);

  useEffect(() => {
    setLoading(true);
  }, [sidebarItem]);

  let body = (
    <div className="flex flex-col gap-1 w-full h-full justify-center items-center">
      <div className="font-style-headline-1 flex items-center mt-[60px]">
        <span>
          Your chats
        </span>
      </div>
      <div className={"font-style-body-md text-gray-3 italic"}>
        This is your inbox, select a chat or start a new one.
      </div>
      <div className="actions flex gap-2 mt-4">
        <Link to={`/compose`} className={getButtonClassName()}>
          Compose
        </Link>
      </div>
    </div>
  );
  if (chatId) {
    body = <ChatPage />;
  }
  return body;
};
